/* eslint-disable react/prop-types */
import "leaflet/dist/leaflet.css";
import L from "leaflet";

import CS from "metabase/css/core/index.css";
import { color } from "metabase/lib/colors";
import { formatValue } from "metabase/lib/formatting";
import { computeMinimalBounds } from "metabase/visualizations/lib/mapping";

import CardRenderer from "./CardRenderer";

const LeafletChoropleth = ({
  series = [],
  geoJson,
  minimalBounds = computeMinimalBounds(geoJson.features || [geoJson]),
  getColor = () => color("brand"),
  onHoverFeature = () => {},
  onClickFeature = () => {},
  onRenderError = () => {},
  showLabelsRegion,
  showLabelsMetric,
  valuesMap,
  columnSettings,
  dimensionIndex,
  nameProperty,
  keyProperty,
}) => (
  <CardRenderer
    card={{ display: "map" }}
    series={series}
    className={CS.spread}
    renderer={(element, props) => {
      element.className = CS.spread;
      element.style.backgroundColor = "transparent";

      const map = L.map(element, {
        attributionControl: false,
        fadeAnimation: false,
        markerZoomAnimation: false,
        trackResize: true,
        worldCopyJump: true,
        zoomAnimation: false,
        zoomSnap: 0,

        // disable zoom controls
        dragging: false,
        tap: false,
        zoomControl: false,
        touchZoom: false,
        doubleClickZoom: false,
        scrollWheelZoom: false,
        boxZoom: false,
        keyboard: false,
      });

      const style = (feature) => ({
        fillColor: getColor(feature),
        weight: 1,
        opacity: 1,
        color: "white",
        fillOpacity: 1,
      });

      const onEachFeature = (feature, layer) => {
        layer.on({
          mousemove: (e) => {
            onHoverFeature({
              feature,
              event: e.originalEvent,
            });
          },
          mouseout: (e) => {
            onHoverFeature(null);
          },
          click: (e) => {
            onClickFeature({
              feature,
              event: e.originalEvent,
            });
          },
        });
      };

      // main layer
      L.featureGroup([
        L.geoJson(geoJson, {
          style,
          onEachFeature,
        }),
      ]).addTo(map);

      // Add labels if enabled
      if ((showLabelsRegion || showLabelsMetric) && valuesMap && columnSettings) {
        const labelLayer = L.featureGroup();
        geoJson.features.forEach((feature) => {
          // Use the keyProperty to get the correct matching key for the feature
          const featureKey = keyProperty
            ? String(feature.properties[keyProperty] || "").toLowerCase()
            : String(
                feature.properties[Object.keys(feature.properties)[0]] || "",
              ).toLowerCase();

          const value = valuesMap[featureKey];

          if (value != null) {
            const labelParts = [];

            if (showLabelsRegion) {
              const featureName = String(
                feature.properties[nameProperty] || "",
              );
              labelParts.push(featureName);
            }

            if (showLabelsMetric) {
              const formattedValue = formatValue(value, columnSettings);
              labelParts.push(formattedValue);
            }

            const labelText = labelParts.join("<br/>");
            const bounds = L.geoJson(feature).getBounds();
            const center = bounds.getCenter();

            const label = L.marker(center, {
              icon: L.divIcon({
                className: "leaflet-label",
                html: `<div style="background-color: rgba(255, 255, 255, 0.95); color: var(--mb-color-text-primary); padding: 4px 8px; border-radius: 4px; font-size: 12px; font-weight: 700; white-space: nowrap; text-align: center; pointer-events: none; border: 1px solid var(--mb-color-border);">${labelText}</div>`,
                iconSize: null,
              }),
            });
            labelLayer.addLayer(label);
          }
        });
        labelLayer.addTo(map);
      }

      map.fitBounds(minimalBounds);

      return () => {
        map.remove();
      };
    }}
    onRenderError={onRenderError}
  />
);

export default LeafletChoropleth;
